// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngQuantityBreakPrice, {
    XngQuantityBreakPriceStyles,
} from "@hardwoods/App/Components/CustomQuantityBreakPrice/XngQuantityBreakPrice";
import { productListCardListBreakpoints } from "@hardwoods/Overrides/Widgets/ProductList/helper";
import ProductListActions, {
    ProductListActionsStyles,
} from "@hardwoods/Overrides/Widgets/ProductList/ProductListActions";
import { ProductListProductImageStyles } from "@hardwoods/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@hardwoods/Overrides/Widgets/ProductList/ProductListProductInformation";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productListProductImage?: ProductListProductImageStyles;
    productActions?: ProductListActionsStyles;
    productInformation?: ProductListProductInformationStyles;
    brandInformation?: ProductListProductInformationStyles;
    sponsoredText?: TypographyPresentationProps;
    qtyBreakPrice?: XngQuantityBreakPriceStyles;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding: 15px;
            width: 265px;
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            background-color: ${color("common.white")};
            box-shadow: 0 0 8px 0 ${color("common.secondary2", 0.2)};

            ${xScreen.lte(productListCardListBreakpoints.tablet)} {
                margin-left: auto;
                margin-right: auto;
            }
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 0 0 15px;
        `,
    },
    productListProductImage: {
        productImage: {
            image: {
                width: "230px",
                height: "230px",
                css: css`
                    border: 0;
                    img {
                        min-width: 100%;
                    }
                `,
            },
        },
    },
    productActions: {
        addToCartAndActionsWrapper: {
            css: css`
                margin-top: auto;
                justify-content: flex-end;
            `,
        },
        addToCartWrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
        addToCartButton: {
            css: css`
                padding: 0 10px;
            `,
        },
        quantityOrdered: {
            qtyField: {
                cssOverrides: {
                    formField: css`
                        margin-top: auto;
                        margin-bottom: auto;
                    `,
                },
            },
            unitOfMeasure: {
                css: css`
                    margin: auto 8px auto 0;
                `,
            },
        },
        priceAndAvailabilityWrapper: {
            css: css`
                margin-top: 2px;
            `,
        },
        addToListAndCompareWrapper: {
            css: css`
                padding-top: 10px;
            `,
        },
    },
    productInformation: {
        container: {
            gap: 10,
            css: css`
                margin-bottom: 15px;
            `,
        },
        descriptionGridItem: {
            width: 12,
            css: css`
                min-height: 54px;
            `,
        },
    },
    brandInformation: {
        container: {
            gap: 0,
            css: css`
                margin-bottom: 10px;
            `,
        },
        brandAndLastPurchasedWrapper: {
            css: css`
                width: 100%;
                justify-content: space-between;
                > a,
                > div {
                    margin-bottom: 4px;
                }
            `,
        },
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    qtyBreakPrice: {
        wraper: {
            css: css`
                border: 1px solid ${getColor("common.border")};
                padding: 4px 8px;
                min-width: 210px;
                margin-bottom: 8px;
            `,
        },
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    productInfo,
}) => {
    const showCompare2 =
        showCompare && (!product.properties.styledChildrenCount || product.properties.styledChildrenCount === "1");

    const productContext = {
        product,
        productInfo,
    };
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            <StyledWrapper {...styles.imageWrapper}>
                <ProductListProductInformation
                    showAttributes={false}
                    showAvailability={false}
                    showBrand={showBrand}
                    showLastPurchasedOn={true}
                    showPartNumbers={false}
                    showTitle={false}
                    showMinPack={false}
                    extendedStyles={styles.brandInformation}
                />
                {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={false}
                    extendedStyles={styles.productListProductImage}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                />
            </StyledWrapper>
            <ProductListProductInformation
                showAttributes={showAttributes}
                showAvailability={false}
                showBrand={false}
                showPartNumbers={showPartNumbers}
                showTitle={showTitle}
                showLastPurchasedOn={false}
                showMinPack={true}
                extendedStyles={styles.productInformation}
                showVariantProps={product.properties.styledChildrenCount === "1"}
                showImageBadges={showImageBadges}
                maxImageBadges={maximumNumberOfImageBadges}
                showTextBadges={showTextBadges}
                maxTextBadges={maximumNumberOfTextBadges}
            />
            <XngQuantityBreakPrice product={productContext} extendedStyles={styles.qtyBreakPrice} />
            <ProductListActions
                showAddToList={showAddToList}
                showPrice={showPrice}
                showCompare={showCompare2}
                extendedStyles={styles.productActions}
            />
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductGridCard);
